import { LitElement, html, css, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import {
  toast,
  buildStatusVariant,
  type BnBadgeVariant,
  type BnColumn,
  type BnRow,
  type BnTagColor,
} from "../components/index.js";
import { navigate } from "../nav.js";
import { detailedError, humanError, parseError } from "../api/errors.js";
import { pb } from "../api/client.js";
import { subscribeLive } from "../api/realtime.js";
import {
  getWorkspace,
  deleteWorkspace,
  listFiles,
  readFile,
  readFileBlob,
  writeFile,
  deleteFile,
  listSecrets,
  setSecret,
  deleteSecret,
  startBuild,
  mintPreviewToken,
  previewUrl,
  setWorkspaceTags,
  type Workspace,
  type WorkspaceFile,
  type SecretName,
} from "../api/workspaces.js";
import { listTags, createTag, type Tag } from "../api/tags.js";
import {
  getBuild,
  listArtifacts,
  getArtifactBlob,
  getLog,
  isTerminal,
  type Build,
  type Artifact,
} from "../api/builds.js";
import {
  getGitStatus,
  connectGit,
  disconnectGit,
  syncGitNow,
  type GitStatus,
} from "../api/git.js";
import {
  listDeployments,
  discoverArtefacts,
  createDeployment,
  updateDeployment,
  publishDeployment,
  stopDeployment,
  startDeployment,
  deleteDeployment,
  mintLiveToken,
  mintViewerLink,
  rotateLinkKey,
  type LiveDeployment,
  type LiveCandidate,
  type LiveStatus,
  type LiveVisibility,
  type UpdateRequest,
} from "../api/live.js";
import { listRepositoryDeployments } from "../api/deployments.js";

const POLL_MS = 3000;

/** IDLE_OPTIONS drives the scale-to-zero `<select>`, in display order. */
const IDLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "300", label: "Sleeps after 5 minutes" },
  { value: "600", label: "Sleeps after 10 minutes" },
  { value: "1800", label: "Sleeps after 30 minutes" },
  { value: "3600", label: "Sleeps after 60 minutes" },
  { value: "0", label: "Always on" },
];

/** SESSION_OPTIONS drives the device-session lifetime `<select>`; 180 days is the server's ceiling. */
const SESSION_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "0", label: "Off" },
  { value: "604800", label: "7 days" },
  { value: "2592000", label: "30 days" },
  { value: "7776000", label: "90 days" },
  { value: "15552000", label: "180 days" },
];

/** TTL_OPTIONS drives the viewer-link lifetime `<select>`; 24 hours is the server's ceiling. */
const TTL_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "900", label: "15 minutes" },
  { value: "3600", label: "1 hour" },
  { value: "21600", label: "6 hours" },
  { value: "86400", label: "24 hours" },
];

/**
 * TAKE_OVER_WARNING ends the connect dialog of a repository: the server moves
 * the default-branch workspace to the branch entered and deletes any other
 * workspace of that branch, pinned or not. A live artefact that is a
 * deployment of the repository outlives the workspace it was published from.
 */
const TAKE_OVER_WARNING =
  "If another workspace already holds this branch, it is deleted with its secrets and " +
  "builds, even when pinned, and this workspace takes the branch over. Its live reports are " +
  "deleted too. Repository deployments stay.";

/** A directory node in the file tree, assembled from the flat path list. */
interface DirNode {
  name: string;
  path: string;
  dirs: Map<string, DirNode>;
  files: WorkspaceFile[];
}

/** localStorage key for the build ids kicked off from this UI for a workspace. */
function buildsKey(id: string): string {
  return `bino-cloud:builds:${id}`;
}

function loadBuildIds(id: string): string[] {
  try {
    const raw = localStorage.getItem(buildsKey(id));
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(arr)) return [];
    // Deduped on read so a history already corrupted by an unguarded insert
    // heals itself on the next load instead of rendering the build twice
    // forever.
    return [...new Set(arr.filter((x): x is string => typeof x === "string"))];
  } catch {
    return [];
  }
}

function saveBuildIds(id: string, ids: string[]): void {
  try {
    localStorage.setItem(buildsKey(id), JSON.stringify(ids));
  } catch {
    /* private mode / quota — history just won't persist */
  }
}

/**
 * Records a build id in the workspace's history unless it is already tracked,
 * and reports whether it was new. Three paths learn about a build — the "Build
 * now" response, a realtime event, and a git sync status — and they race each
 * other (the server saves the record before answering, so the realtime create
 * usually lands first), so every one of them must go through this.
 */
function trackBuildId(wsId: string, id: string): boolean {
  const ids = loadBuildIds(wsId);
  if (ids.includes(id)) return false;
  saveBuildIds(wsId, [id, ...ids]);
  return true;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** idleLabel renders a deployment's scale-to-zero setting; 0 is always-on. */
function idleLabel(seconds: number): string {
  return seconds <= 0 ? "Always on" : `Sleeps after ${Math.round(seconds / 60)} minutes`;
}

/** sessionLabel renders a deployment's device-session lifetime; 0 is off. */
function sessionLabel(seconds: number): string {
  if (seconds <= 0) return "Off";
  const days = Math.max(1, Math.round(seconds / 86400));
  return days === 1 ? "1 day" : `${days} days`;
}

/** liveStatusVariant colours the deployment lifecycle badge. */
function liveStatusVariant(status: LiveStatus): BnBadgeVariant {
  return status === "live" ? "success" : status === "stopped" ? "warning" : "neutral";
}

/**
 * canOpen says whether this UI can put a working URL in a new tab. A team
 * deployment gets a freshly minted short-lived token in `?t=`, a public one
 * needs nothing — but a "token"-visibility deployment is gated on the
 * CUSTOMER's own access token, of which bino Cloud kept only the SHA-256. There
 * is nothing to attach, the bare URL answers 401, and no button here can
 * recover from that: offering one would be a dead end, so the embed snippet's
 * `?t=` form is where that deployment's URL is explained instead.
 */
function canOpen(d: LiveDeployment): boolean {
  return d.status === "live" && !!d.url && d.visibility !== "token";
}

/** parseHosts splits the space- or comma-separated embed-domain input. */
function parseHosts(text: string): string[] {
  return text.split(/[\s,]+/).filter((h) => h !== "");
}

/**
 * parseParams reads the `name=value` lines of the viewer-link form into the
 * parameters the link pins. Lines without a name are skipped rather than
 * rejected — the server validates names and values, and its message is the one
 * worth showing.
 */
function parseParams(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split("\n")) {
    const eq = line.indexOf("=");
    if (eq <= 0) continue;
    out[line.slice(0, eq).trim()] = line.slice(eq + 1).trim();
  }
  return out;
}

/**
 * page-workspace is the /workspaces/:id detail. It stacks sections built on the
 * component kit: a file browser (tree + viewer/editor, create, upload,
 * download, delete), a git-sync card (connect/disconnect a repo, sync now,
 * webhook credentials — connected workspaces lock file writes), a write-only
 * secrets panel, a build action with live history (log + artifact download),
 * an open-preview action, and a live-artefacts card (publish a
 * LiveReportArtefact to a permanent public URL, then run/stop/start it, change
 * how it is served, hand out viewer links and embed it). Build history is tracked client-side in
 * localStorage; ids also arrive via realtime create events (webhook-triggered
 * builds), with 3s polling as the fallback when no stream can be established.
 */
@customElement("page-workspace")
export class PageWorkspace extends LitElement {
  @property() workspaceId = "";

  // Workspace shell
  @state() private loading = true;
  @state() private error = "";
  @state() private workspace: Workspace | null = null;
  @state() private confirmDeleteWs = false;
  @state() private deletingWs = false;

  // Tags. The workspace's own tags arrive embedded on the record; the catalog is
  // the team's full vocabulary, needed only to offer the ones not yet attached.
  @state() private tagCatalog: Tag[] = [];
  @state() private showTags = false;
  @state() private tagBusy = false;
  @state() private newTagName = "";
  @state() private newTagColor: BnTagColor = "gray";
  @state() private creatingTag = false;
  @state() private tagError = "";

  // Files
  @state() private files: WorkspaceFile[] = [];
  @state() private filesLoading = false;
  @state() private expanded = new Set<string>();
  @state() private selected = "";
  @state() private fileContent = "";
  @state() private fileBinary = false;
  @state() private fileLoading = false;
  @state() private editing = false;
  @state() private editContent = "";
  // Set when a save found that someone else changed the open file.
  @state() private editConflict = false;
  @state() private savingFile = false;
  @state() private pendingFileDelete = "";
  @state() private showNewFile = false;
  @state() private newFilePath = "";
  @state() private newFileContent = "";
  @state() private newFileError = "";
  @state() private creatingFile = false;
  @state() private uploading = false;
  // The commit the file list was read at, and the one the open file was read
  // at. Writes send them back so a stale edit is refused (commit_conflict)
  // instead of overwriting a newer one. Both stay null for a workspace without
  // a repository, and then nothing is sent.
  @state() private baseCommit: string | null = null;
  @state() private selectedBase: string | null = null;

  // Secrets — the workspace's own plus the team secrets it inherits, as the
  // server merges them (see listSecrets).
  @state() private secrets: SecretName[] = [];
  @state() private secretsLoading = false;
  @state() private showAddSecret = false;
  @state() private secretName = "";
  @state() private secretValue = "";
  @state() private secretError = "";
  @state() private savingSecret = false;
  @state() private pendingSecretDelete = "";

  // Git sync
  @state() private git: GitStatus | null = null;
  @state() private showGitModal = false;
  @state() private gitRepoUrl = "";
  @state() private gitBranch = "main";
  @state() private gitToken = "";
  @state() private gitError = "";
  @state() private savingGit = false;
  @state() private confirmGitDisconnect = false;
  @state() private disconnectingGit = false;
  @state() private syncingGit = false;
  private gitPollTimer?: number;
  private stopGitLive?: () => void;
  private gitLiveUp = false;
  private gitLiveRepo = "";

  // Builds
  @state() private builds: Build[] = [];
  @state() private buildsLoading = false;
  @state() private building = false;
  private pollTimer?: number;
  private stopBuildsLive?: () => void;
  private buildsLiveUp = false;

  // Build detail modal
  @state() private detailBuild: Build | null = null;
  @state() private detailLog = "";
  @state() private detailArtifacts: Artifact[] = [];
  @state() private detailLoading = false;

  // Preview
  @state() private openingPreview = false;

  // Live artefacts — the deployment rows come from the cheap listing route, so
  // the card and the header's Run button cost one plain query. Discovery is
  // reached only through the publish modal (see openPublish).
  @state() private deployments: LiveDeployment[] = [];
  @state() private deploymentsLoading = false;
  @state() private liveBusy = "";
  @state() private openingLive = "";
  @state() private pendingLiveDelete: LiveDeployment | null = null;
  // Whether the server has deployments on and this workspace belongs to a
  // repository: publishing then happens on the repository's deployments
  // page, at a commit, and the publish modal below is not used.
  @state() private deploymentsOn = false;

  // Publish modal, and the settings modal — which reuses the serving fields
  // below, because it is the same form minus the artefact picker and only one
  // of the two can be open.
  @state() private showPublish = false;
  @state() private editTarget: LiveDeployment | null = null;
  @state() private savingEdit = false;
  @state() private candidates: LiveCandidate[] = [];
  @state() private candidatesLoading = false;
  @state() private pubArtefact = "";
  @state() private pubVisibility: LiveVisibility = "team";
  @state() private pubToken = "";
  @state() private pubEmbed = "";
  @state() private pubIdle = "600";
  @state() private pubSessionTtl = "2592000";
  @state() private pubError = "";
  @state() private publishing = false;
  // The access token's one and only appearance, held no longer than the panel
  // that shows it — closePublish drops it and nothing can print it again.
  @state() private publishedToken = "";

  // Viewer links
  @state() private linkTarget: LiveDeployment | null = null;
  @state() private linkTtl = "3600";
  @state() private linkParams = "";
  @state() private linkError = "";
  @state() private mintingLink = false;
  @state() private mintedLink = "";
  @state() private pendingLinkRotate: LiveDeployment | null = null;
  @state() private rotatingLinkKey = false;

  static override styles = css`
    :host {
      display: block;
    }
    .crumbs {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
    }
    .crumbs a {
      color: var(--bino-text-secondary);
      text-decoration: none;
    }
    .crumbs a:hover {
      color: var(--bino-text);
    }
    .head {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    /* Title column: the name, then the tag row beneath it. */
    .titles {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
      min-width: 0;
    }
    .tag-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: var(--bino-space-xs);
    }
    .repo-row {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .repo-row a {
      color: var(--bino-text-secondary);
    }
    .head h1 {
      margin: 0;
      font-family: var(--bino-font-sans);
      font-size: 1.375rem;
      font-weight: 600;
      color: var(--bino-text);
      word-break: break-word;
    }
    .head .spacer {
      flex: 1;
    }
    .sections {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-lg);
    }
    .actions-row {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }

    /* File browser */
    .browser {
      display: grid;
      grid-template-columns: var(--bino-sidebar-width) 1fr;
      min-height: 18rem;
    }
    @media (max-width: 720px) {
      .browser {
        grid-template-columns: 1fr;
      }
    }
    .tree {
      border-right: 1px solid var(--bino-border);
      overflow: auto;
      max-height: 28rem;
      padding: var(--bino-space-sm);
    }
    @media (max-width: 720px) {
      .tree {
        border-right: none;
        border-bottom: 1px solid var(--bino-border);
        max-height: 16rem;
      }
    }
    .node {
      display: flex;
      align-items: center;
      gap: var(--bino-space-xs);
      width: 100%;
      box-sizing: border-box;
      border: none;
      background: none;
      text-align: left;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      padding: 0.25rem 0.375rem;
      border-radius: var(--bino-radius-sm);
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .node:hover {
      background: var(--bino-surface-hover);
    }
    .node.active {
      background: var(--bino-surface-active);
      color: var(--bino-active-text);
    }
    .node:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--bino-focus-ring);
    }
    .caret {
      display: inline-block;
      width: 0.75rem;
      color: var(--bino-text-secondary);
      flex-shrink: 0;
    }
    .glyph {
      color: var(--bino-text-secondary);
      flex-shrink: 0;
    }
    .node .lbl {
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .viewer {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }
    .viewer-head {
      display: flex;
      align-items: center;
      gap: var(--bino-space-sm);
      padding: var(--bino-space-sm) var(--bino-space-md);
      border-bottom: 1px solid var(--bino-border);
    }
    .viewer-path {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
    }
    .viewer-size {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      flex-shrink: 0;
    }
    pre.content {
      margin: 0;
      padding: var(--bino-space-md);
      overflow: auto;
      max-height: 26rem;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      line-height: 1.5;
      color: var(--bino-text);
      background: var(--bino-surface-subtle);
      white-space: pre;
    }
    textarea.editor {
      margin: 0;
      border: none;
      resize: vertical;
      min-height: 20rem;
      padding: var(--bino-space-md);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      line-height: 1.5;
      color: var(--bino-text);
      background: var(--bino-surface-subtle);
    }
    textarea.editor:focus {
      outline: none;
      box-shadow: inset 0 0 0 2px var(--bino-focus-ring);
    }
    .viewer-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 1;
      padding: var(--bino-space-xl);
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      text-align: center;
    }

    /* modal helpers */
    .modal-textarea {
      width: 100%;
      box-sizing: border-box;
      min-height: 10rem;
      resize: vertical;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
    }
    .modal-textarea:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
    pre.log {
      margin: 0;
      padding: var(--bino-space-md);
      overflow: auto;
      max-height: 22rem;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      line-height: 1.5;
      color: var(--bino-text);
      background: var(--bino-surface-inset);
      border-radius: var(--bino-radius);
      white-space: pre-wrap;
      word-break: break-word;
    }
    .detail-meta {
      display: flex;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-md);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .section-label {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--bino-primary);
      margin: var(--bino-space-md) 0 var(--bino-space-sm);
    }
    .hint {
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      margin: 0 0 var(--bino-space-md);
    }
    .link-btn {
      background: none;
      border: none;
      padding: 0;
      cursor: pointer;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-primary);
      text-decoration: underline;
    }
    .link-btn:hover {
      color: var(--bino-primary-hover);
    }

    /* Git sync */
    .git-rows {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
    }
    .git-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }
    .git-label {
      min-width: 6.5rem;
      color: var(--bino-text-secondary);
    }
    .mono {
      font-family: var(--bino-font-mono);
    }
    .git-banner {
      padding: var(--bino-space-sm) var(--bino-space-md);
      border-bottom: 1px solid var(--bino-border);
      background: var(--bino-info-bg);
      color: var(--bino-info);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
    }
    /* Copyable webhook credentials (same surface as page-api-keys). */
    .copy-row {
      display: flex;
      align-items: stretch;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-sm);
    }
    .copy-row .code {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      margin: 0;
      padding: var(--bino-space-sm) var(--bino-space-md);
      background: var(--bino-surface-inset);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      white-space: nowrap;
      overflow-x: auto;
    }
    .warn {
      display: flex;
      gap: var(--bino-space-sm);
      align-items: flex-start;
      margin-top: var(--bino-space-md);
      padding: var(--bino-space-md);
      background: var(--bino-warning-bg);
      border: 1px solid var(--bino-warning-border);
      border-radius: var(--bino-radius);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-warning-text);
    }
    .warn-icon {
      flex-shrink: 0;
      font-weight: 700;
    }
    .warn.edit-conflict {
      margin: var(--bino-space-sm) var(--bino-space-md) 0;
    }

    /* Live artefacts: one block per deployment, separated by a hairline. The
       label/value rows repeat the git card's recipe rather than sharing its
       classes, so neither card's layout is hostage to the other's. */
    .deploy + .deploy {
      margin-top: var(--bino-space-lg);
      padding-top: var(--bino-space-lg);
      border-top: 1px solid var(--bino-border);
    }
    .deploy-head {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-sm);
    }
    .deploy-name {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      word-break: break-all;
    }
    .deploy-rows {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
      margin-bottom: var(--bino-space-md);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
    }
    .deploy-row {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: var(--bino-space-sm);
    }
    .deploy-label {
      min-width: 7.5rem;
      color: var(--bino-text-secondary);
    }
    /* The embed and curl snippets are multi-line, so they sit in the copy row
       as pre.log rather than as the single-line .code span. */
    .copy-row pre.log {
      flex: 1;
      min-width: 0;
    }

    /* Native selects in the publish and viewer-link modals (same recipe as
       page-api-keys' create modals). */
    select {
      width: 100%;
      box-sizing: border-box;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text);
      padding: 0.5rem 0.6875rem;
      border: 1px solid var(--bino-border-light);
      border-radius: var(--bino-radius);
      background: var(--bino-surface-subtle);
    }
    select:focus {
      outline: none;
      border-color: var(--bino-primary);
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopPoll();
    this.stopGitPoll();
    this.stopBuildsSubscription();
    this.stopGitSubscription();
  }

  private async load() {
    this.loading = true;
    this.error = "";
    try {
      this.workspace = await getWorkspace(this.workspaceId);
    } catch (e) {
      this.error = humanError(e);
      this.loading = false;
      return;
    }
    this.loading = false;
    this.startBuildsSubscription();
    this.startGitSubscription();
    void this.loadFiles();
    void this.loadGit();
    void this.loadSecrets();
    void this.loadBuilds();
    void this.loadDeployments();
    void this.probeDeployments();
    void this.loadTagCatalog();
  }

  /**
   * probeDeployments asks the repository's deployments once to learn whether
   * the server has deployments on. A failure leaves the publish modal in
   * place, as with deployments off.
   */
  private async probeDeployments() {
    const repo = this.workspace?.repository;
    this.deploymentsOn = false;
    if (!repo) return;
    try {
      const deps = await listRepositoryDeployments(repo);
      if (repo === this.workspace?.repository) this.deploymentsOn = deps !== null;
    } catch {
      /* the modal stays */
    }
  }

  // ---- Tags ----

  private async loadTagCatalog() {
    const teamId = this.workspace?.teamId;
    if (!teamId) return;
    try {
      this.tagCatalog = await listTags(teamId);
    } catch (e) {
      // The workspace's own tags are already on the record, so a failed catalog
      // costs only the "add more" list — toast rather than blanking the page.
      toast(humanError(e), "error");
    }
  }

  /**
   * setTags PATCHes the whole array — the API has no add/remove verb. Applied
   * optimistically and reverted on failure, so a chip never lingers on a tag the
   * server refused (a viewer, or a tag a teammate deleted meanwhile).
   */
  private async setTags(next: Tag[]) {
    const ws = this.workspace;
    if (!ws || this.tagBusy) return;
    this.workspace = { ...ws, tags: next };
    this.tagBusy = true;
    try {
      this.workspace = await setWorkspaceTags(
        ws.id,
        next.map((t) => t.id),
      );
    } catch (e) {
      this.workspace = ws;
      toast(humanError(e), "error");
    } finally {
      this.tagBusy = false;
    }
  }

  private openTagEditor() {
    this.newTagName = "";
    this.newTagColor = "gray";
    this.tagError = "";
    this.showTags = true;
    void this.loadTagCatalog();
  }

  /**
   * createAndAttach coins a tag and puts it on this workspace in one go: the
   * moment you want a tag is the moment you are tagging something, and bouncing
   * to the team page mid-flow would lose the workspace context.
   */
  private async createAndAttach() {
    const ws = this.workspace;
    if (!ws) return;
    const name = this.newTagName.trim();
    if (!name) {
      this.tagError = "Enter a tag name.";
      return;
    }
    this.creatingTag = true;
    this.tagError = "";
    try {
      const tag = await createTag(ws.teamId, name, this.newTagColor);
      this.tagCatalog = [...this.tagCatalog, tag];
      this.newTagName = "";
      await this.setTags([...(this.workspace?.tags ?? []), tag]);
    } catch (e) {
      this.tagError = humanError(e);
    } finally {
      this.creatingTag = false;
    }
  }

  // ---- Files ----

  private async loadFiles() {
    this.filesLoading = true;
    try {
      const { items, commit } = await listFiles(this.workspaceId);
      this.files = items;
      this.baseCommit = commit;
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.filesLoading = false;
    }
  }

  private buildTree(): DirNode {
    const root: DirNode = { name: "", path: "", dirs: new Map(), files: [] };
    for (const f of this.files) {
      const segs = f.path.split("/");
      let node = root;
      for (let i = 0; i < segs.length - 1; i++) {
        const seg = segs[i]!;
        const dirPath = node.path ? `${node.path}/${seg}` : seg;
        let child = node.dirs.get(seg);
        if (!child) {
          child = { name: seg, path: dirPath, dirs: new Map(), files: [] };
          node.dirs.set(seg, child);
        }
        node = child;
      }
      node.files.push(f);
    }
    return root;
  }

  private renderTree(node: DirNode, depth: number): TemplateResult[] {
    const out: TemplateResult[] = [];
    const dirs = [...node.dirs.values()].sort((a, b) => a.name.localeCompare(b.name));
    for (const d of dirs) {
      const open = this.expanded.has(d.path);
      out.push(html`<button
        class="node"
        style=${`padding-left: calc(var(--bino-space-sm) + var(--bino-space-md) * ${depth})`}
        @click=${() => this.toggleDir(d.path)}
        aria-expanded=${open ? "true" : "false"}
      >
        <span class="caret" aria-hidden="true">${open ? "▾" : "▸"}</span>
        <span class="glyph" aria-hidden="true">▤</span>
        <span class="lbl">${d.name}</span>
      </button>`);
      if (open) out.push(...this.renderTree(d, depth + 1));
    }
    const files = [...node.files].sort((a, b) => a.path.localeCompare(b.path));
    for (const f of files) {
      const name = f.path.split("/").pop() ?? f.path;
      out.push(html`<button
        class="node ${this.selected === f.path ? "active" : ""}"
        style=${`padding-left: calc(var(--bino-space-sm) + var(--bino-space-md) * ${depth})`}
        @click=${() => this.selectFile(f.path)}
      >
        <span class="caret" aria-hidden="true"></span>
        <span class="glyph" aria-hidden="true">◦</span>
        <span class="lbl">${name}</span>
      </button>`);
    }
    return out;
  }

  private toggleDir(path: string) {
    const next = new Set(this.expanded);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    this.expanded = next;
  }

  private async selectFile(path: string) {
    this.selected = path;
    this.editing = false;
    this.fileBinary = false;
    this.fileContent = "";
    this.fileLoading = true;
    try {
      const { text, commit } = await readFile(this.workspaceId, path);
      this.selectedBase = commit;
      if (text.includes("\u0000")) {
        this.fileBinary = true;
      } else {
        this.fileContent = text;
      }
    } catch (e) {
      toast(humanError(e), "error");
      this.selected = "";
    } finally {
      this.fileLoading = false;
    }
  }

  private startEdit() {
    this.editContent = this.fileContent;
    this.editConflict = false;
    this.editing = true;
  }

  /**
   * setCommit makes the commit a write produced the base of the next one. The
   * open file's base moves only when the write was based on it: after a write
   * based on a newer file list, the open file still shows what it was read at,
   * and its save must be refused if someone changed it since.
   */
  private setCommit(commit: string | null, base: string | null) {
    this.baseCommit = commit;
    if (base === this.selectedBase) this.selectedBase = commit;
  }

  /**
   * reloadOnConflict refreshes the file list after a write was refused because
   * the files changed since they were read, so the next try has a fresh base.
   */
  private reloadOnConflict(e: unknown) {
    if (parseError(e).code === "commit_conflict") void this.loadFiles();
  }

  private async saveEdit() {
    this.savingFile = true;
    const path = this.selected;
    const base = this.selectedBase;
    try {
      const { file: meta, commit } = await writeFile(
        this.workspaceId,
        path,
        this.editContent,
        base,
      );
      this.setCommit(commit, base);
      this.fileContent = this.editContent;
      this.editing = false;
      this.editConflict = false;
      this.files = this.files.map((f) => (f.path === meta.path ? meta : f));
      toast("File saved", "success");
    } catch (e) {
      // The editor stays open with the user's text, so nothing is lost.
      if (parseError(e).code === "commit_conflict") {
        void this.loadFiles();
        await this.rebaseEdit(path);
      } else {
        toast(humanError(e), "error");
      }
    } finally {
      this.savingFile = false;
    }
  }

  /**
   * rebaseEdit follows a save of the open file that was refused with
   * commit_conflict. The server compares the base with the whole branch, so a
   * change to any other file refuses the save too, and a retry with the same
   * base would be refused forever. The file is read again and its commit
   * becomes the base. When the file still holds what the edit started from,
   * the next Save goes through. When someone else changed it, the editor keeps
   * the user's text and says so: Save then replaces their version, and Cancel
   * shows it.
   */
  private async rebaseEdit(path: string) {
    try {
      const { text, commit } = await readFile(this.workspaceId, path);
      if (this.selected !== path || !this.editing) return;
      this.selectedBase = commit;
      if (text === this.fileContent) {
        toast(
          "Other files changed since you opened this one. Save again to keep your edit.",
          "info",
        );
        return;
      }
      this.fileContent = text;
      this.editConflict = true;
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private async downloadSelected() {
    try {
      const blob = await readFileBlob(this.workspaceId, this.selected);
      downloadBlob(blob, this.selected.split("/").pop() ?? this.selected);
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private async confirmFileDelete() {
    const path = this.pendingFileDelete;
    const base = this.baseCommit;
    try {
      const { commit } = await deleteFile(this.workspaceId, path, base);
      this.setCommit(commit, base);
      this.files = this.files.filter((f) => f.path !== path);
      if (this.selected === path) {
        this.selected = "";
        this.fileContent = "";
      }
      toast("File deleted", "success");
    } catch (e) {
      toast(humanError(e), "error");
      this.reloadOnConflict(e);
    } finally {
      this.pendingFileDelete = "";
    }
  }

  private async submitNewFile() {
    const path = this.newFilePath.trim().replace(/^\/+/, "");
    if (!path) {
      this.newFileError = "Enter a file path.";
      return;
    }
    this.creatingFile = true;
    this.newFileError = "";
    const base = this.baseCommit;
    try {
      const { file: meta, commit } = await writeFile(
        this.workspaceId,
        path,
        this.newFileContent,
        base,
      );
      this.setCommit(commit, base);
      this.files = [...this.files.filter((f) => f.path !== meta.path), meta];
      this.showNewFile = false;
      this.newFilePath = "";
      this.newFileContent = "";
      toast("File created", "success");
      void this.selectFile(meta.path);
    } catch (e) {
      this.newFileError = humanError(e);
      this.reloadOnConflict(e);
    } finally {
      this.creatingFile = false;
    }
  }

  private async onUpload(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    this.uploading = true;
    const base = this.baseCommit;
    try {
      const { file: meta, commit } = await writeFile(
        this.workspaceId,
        file.name,
        file,
        base,
      );
      this.setCommit(commit, base);
      this.files = [...this.files.filter((f) => f.path !== meta.path), meta];
      toast(`Uploaded ${file.name}`, "success");
    } catch (err) {
      toast(humanError(err), "error");
      this.reloadOnConflict(err);
    } finally {
      this.uploading = false;
    }
  }

  private renderViewer() {
    if (!this.selected) {
      return html`<div class="viewer-placeholder">Select a file to view its contents.</div>`;
    }
    const size = this.files.find((f) => f.path === this.selected)?.size ?? 0;
    return html`
      <div class="viewer-head">
        <span class="viewer-path" title=${this.selected}>${this.selected}</span>
        <span class="viewer-size">${formatBytes(size)}</span>
        ${this.editing
          ? html`<bn-button
                size="sm"
                variant="ghost"
                @click=${() => (this.editing = false)}
                >Cancel</bn-button
              >
              <bn-button
                size="sm"
                variant="primary"
                ?loading=${this.savingFile}
                @click=${() => this.saveEdit()}
                >Save</bn-button
              >`
          : html`${this.gitLocked
                ? nothing
                : html`<bn-button
                    size="sm"
                    variant="ghost"
                    ?disabled=${this.fileBinary || this.fileLoading}
                    @click=${() => this.startEdit()}
                    >Edit</bn-button
                  >`}
              <bn-button size="sm" variant="ghost" @click=${() => this.downloadSelected()}
                >Download</bn-button
              >
              ${this.gitLocked
                ? nothing
                : html`<bn-button
                    size="sm"
                    variant="danger"
                    @click=${() => (this.pendingFileDelete = this.selected)}
                    >Delete</bn-button
                  >`}`}
      </div>
      ${this.fileLoading
        ? html`<bn-spinner block label="Loading file…"></bn-spinner>`
        : this.editing
          ? html`${this.editConflict
                ? html`<div class="warn edit-conflict" role="alert">
                    <span class="warn-icon" aria-hidden="true">!</span>
                    <span
                      >Someone else changed this file while you edited it. Save replaces their
                      version with your text. Cancel shows their version.</span
                    >
                  </div>`
                : nothing}
              <textarea
                class="editor"
                .value=${this.editContent}
                @input=${(e: Event) => (this.editContent = (e.target as HTMLTextAreaElement).value)}
                spellcheck="false"
              ></textarea>`
          : this.fileBinary
            ? html`<div class="viewer-placeholder">
                Binary file (${formatBytes(size)}). Download it to open it.
              </div>`
            : html`<pre class="content">${this.fileContent}</pre>`}
    `;
  }

  private renderFiles() {
    const tree = this.buildTree();
    return html`<bn-card heading="Files" flush>
      <div slot="actions" class="actions-row">
        <bn-button size="sm" variant="ghost" @click=${() => this.loadFiles()}>Refresh</bn-button>
        ${this.gitLocked
          ? nothing
          : html`<bn-button size="sm" variant="secondary" ?loading=${this.uploading} @click=${() =>
              this.uploadInput?.click()}>Upload</bn-button>
            <bn-button
              size="sm"
              variant="primary"
              @click=${() => {
                this.newFilePath = "";
                this.newFileContent = "";
                this.newFileError = "";
                this.showNewFile = true;
              }}
              >New file</bn-button
            >`}
      </div>
      <input
        type="file"
        style="display:none"
        @change=${(e: Event) => this.onUpload(e)}
      />
      ${this.gitLocked
        ? html`<div class="git-banner">
            Files are synced from
            <span class="mono">${this.git?.repoUrl}@${this.workspace?.branch ?? this.git?.branch}</span>.
            ${this.gitManaged
              ? "Change them there, or disconnect to edit them here."
              : html`Change them there, or disconnect the repository from its default-branch
                  workspace to edit them here.`}
          </div>`
        : nothing}
      ${this.filesLoading && this.files.length === 0
        ? html`<bn-spinner block label="Loading files…"></bn-spinner>`
        : this.files.length === 0
          ? html`<bn-empty-state
              icon="◲"
              heading="No files"
              message="Create or upload a file."
            ></bn-empty-state>`
          : html`<div class="browser">
              <div class="tree">${this.renderTree(tree, 0)}</div>
              <div class="viewer">${this.renderViewer()}</div>
            </div>`}
    </bn-card>`;
  }

  private get uploadInput(): HTMLInputElement | null {
    return this.renderRoot.querySelector('input[type="file"]');
  }

  // ---- Git sync ----

  /** While connected the server rejects file writes (409 workspace_git_locked). */
  private get gitLocked(): boolean {
    return this.git?.connected === true;
  }

  private async loadGit() {
    try {
      this.applyGitStatus(await getGitStatus(this.workspaceId));
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  /**
   * applyGitStatus lands a fresh status: tracks the sync-triggered build in the
   * localStorage history (the Builds card only knows ids tracked client-side),
   * refreshes the file tree when a live sync just settled (the tree was
   * swapped), and starts/stops the git poll.
   */
  private applyGitStatus(status: GitStatus) {
    const wasLive = this.gitSyncLive(this.git);
    this.git = status;
    if (status.lastBuildId && trackBuildId(this.workspaceId, status.lastBuildId)) {
      void this.loadBuilds();
    }
    if (wasLive && !this.gitSyncLive(status)) void this.loadFiles();
    if (this.isConnected && (status.repository ?? "") !== this.gitLiveRepo) {
      this.startGitSubscription();
    }
    this.scheduleGitPoll();
  }

  /** The repository the workspace's git state lives on; "" without one. */
  private get gitRepository(): string {
    return this.git ? (this.git.repository ?? "") : (this.workspace?.repository ?? "");
  }

  private gitSyncLive(g: GitStatus | null): boolean {
    return (
      g?.connected === true && (g.lastSyncStatus === "pending" || g.lastSyncStatus === "syncing")
    );
  }

  /**
   * Subscribes to the workspace's workspace_git realtime events, routing each
   * through pollGit -> applyGitStatus so the settle side effects (file-tree
   * reload, build merge) fire as before. Collection topic + filter because the
   * UI never learns the row id and the row is created/deleted on
   * connect/disconnect. Polling remains the fallback. A workspace of a
   * repository has its sync state on the repository row instead, which lives
   * as long as the repository, so that row is watched by id.
   */
  private startGitSubscription() {
    this.stopGitSubscription();
    const repo = this.gitRepository;
    this.gitLiveRepo = repo;
    this.stopGitLive = subscribeLive({
      ...(repo
        ? { collection: "repositories", topic: repo }
        : {
            collection: "workspace_git",
            topic: "*",
            filter: pb.filter("workspace = {:id}", { id: this.workspaceId }),
          }),
      fields: "id,last_sync_status",
      onEvent: () => void this.pollGit(),
      onUp: () => {
        this.gitLiveUp = true;
        this.stopGitPoll();
        void this.pollGit();
      },
      onDown: () => this.scheduleGitPoll(),
    });
  }

  private stopGitSubscription() {
    this.stopGitLive?.();
    this.stopGitLive = undefined;
    this.gitLiveUp = false;
  }

  private scheduleGitPoll() {
    if (this.gitLiveUp) return;
    if (this.gitSyncLive(this.git)) {
      if (this.gitPollTimer === undefined) {
        this.gitPollTimer = window.setInterval(() => void this.pollGit(), POLL_MS);
      }
    } else {
      this.stopGitPoll();
    }
  }

  private stopGitPoll() {
    if (this.gitPollTimer !== undefined) {
      window.clearInterval(this.gitPollTimer);
      this.gitPollTimer = undefined;
    }
  }

  private async pollGit() {
    try {
      this.applyGitStatus(await getGitStatus(this.workspaceId));
    } catch {
      /* transient poll failure — keep polling */
    }
  }

  private openGitModal() {
    const g = this.git;
    this.gitRepoUrl = g?.connected ? (g.repoUrl ?? "") : "";
    // For a repository the status names its default branch, connected or not.
    this.gitBranch = g?.branch || "main";
    this.gitToken = "";
    this.gitError = "";
    this.showGitModal = true;
  }

  private async submitGit() {
    const repoUrl = this.gitRepoUrl.trim();
    if (!repoUrl) {
      this.gitError = "Enter a repository URL.";
      return;
    }
    const wasConnected = this.gitLocked;
    this.savingGit = true;
    this.gitError = "";
    try {
      const req: { repoUrl: string; branch?: string; token?: string } = { repoUrl };
      const branch = this.gitBranch.trim();
      if (branch) req.branch = branch;
      if (this.gitToken) req.token = this.gitToken;
      this.applyGitStatus(await connectGit(this.workspaceId, req));
      this.showGitModal = false;
      this.gitToken = "";
      toast(
        wasConnected
          ? "Connection updated. Sync started."
          : this.gitRepository
            ? "Git forge connected. First sync started."
            : "Repository connected. First sync started.",
        "success",
      );
    } catch (e) {
      this.gitError = humanError(e);
    } finally {
      this.savingGit = false;
    }
  }

  private async gitSyncNow() {
    this.syncingGit = true;
    try {
      await syncGitNow(this.workspaceId);
      // The sync is queued async; flip to pending locally so the poll starts
      // even before the runner has written "syncing".
      if (this.git?.connected) this.git = { ...this.git, lastSyncStatus: "pending" };
      this.scheduleGitPoll();
      toast("Sync started", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.syncingGit = false;
    }
  }

  private async confirmGitDisconnectNow() {
    this.disconnectingGit = true;
    try {
      await disconnectGit(this.workspaceId);
      const repository = this.git?.repository;
      // A repository keeps its default branch; the connect dialog starts from it.
      this.git = repository
        ? { connected: false, repository, branch: this.git?.branch }
        : { connected: false };
      this.stopGitPoll();
      toast(repository ? "Git forge disconnected" : "Repository disconnected", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.disconnectingGit = false;
      this.confirmGitDisconnect = false;
    }
  }

  private async copy(text: string, label: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast(label, "success");
    } catch {
      toast("Couldn't copy. Select the text and copy it yourself.", "error");
    }
  }

  /**
   * A repository connects to or disconnects from a forge as a whole, and the
   * server takes that only from its default-branch workspace. A workspace
   * without a repository manages its own connection.
   */
  private get gitManaged(): boolean {
    return !this.gitRepository || this.workspace?.defaultBranch === true;
  }

  private renderGit() {
    const g = this.git;
    const repo = this.gitRepository;
    return html`<bn-card heading=${repo ? "Git forge" : "Git sync"}>
      ${g?.connected
        ? html`<div slot="actions" class="actions-row">
            <bn-button
              size="sm"
              variant="secondary"
              ?loading=${this.syncingGit}
              ?disabled=${this.gitSyncLive(g)}
              @click=${() => this.gitSyncNow()}
              >Sync now</bn-button
            >
            ${this.gitManaged
              ? html`<bn-button size="sm" variant="ghost" @click=${() => this.openGitModal()}
                    >Edit</bn-button
                  >
                  <bn-button
                    size="sm"
                    variant="danger"
                    @click=${() => (this.confirmGitDisconnect = true)}
                    >Disconnect</bn-button
                  >`
              : nothing}
          </div>`
        : nothing}
      ${!g
        ? html`<bn-spinner block label="Loading git status…"></bn-spinner>`
        : g.connected
          ? this.renderGitConnected(g)
          : this.renderGitDisconnected(repo)}
    </bn-card>`;
  }

  private renderGitDisconnected(repo: string) {
    if (!repo) {
      return html`<p class="hint">
          Connect a git repository to sync its contents into this workspace on every push.
        </p>
        <bn-button variant="primary" @click=${() => this.openGitModal()}
          >Connect repository</bn-button
        >`;
    }
    if (!this.gitManaged) {
      return html`<p class="hint">
        This repository is hosted here. A git forge is connected from its default-branch
        workspace.
      </p>`;
    }
    return html`<p class="hint">
        This repository is hosted here. Connect a repository on a git forge to mirror it
        instead.
      </p>
      <bn-button variant="primary" @click=${() => this.openGitModal()}
        >Connect git forge</bn-button
      >`;
  }

  private renderGitConnected(g: GitStatus) {
    const live = this.gitSyncLive(g);
    const variant: BnBadgeVariant =
      g.lastSyncStatus === "succeeded" ? "success" : g.lastSyncStatus === "failed" ? "error" : "info";
    return html`
      <div class="git-rows">
        <div class="git-row">
          <span class="git-label">${g.repository ? "Forge" : "Repository"}</span>
          <span class="mono">${g.repoUrl} @ ${g.branch}</span>
        </div>
        <div class="git-row">
          <span class="git-label">Last sync</span>
          <bn-badge .variant=${variant} ?dot=${live}>${g.lastSyncStatus}</bn-badge>
          ${g.lastSyncSha ? html`<span class="mono">${g.lastSyncSha.slice(0, 8)}</span>` : nothing}
          ${g.lastSyncAt ? html`<span>${new Date(g.lastSyncAt).toLocaleString()}</span>` : nothing}
        </div>
        ${g.lastSyncStatus === "failed" && g.lastSyncError
          ? html`<p class="hint" style="color:var(--bino-error)">${g.lastSyncError}</p>`
          : nothing}
      </div>
      ${g.webhookUrl
        ? html`
            <div class="section-label">Push webhook</div>
            <p class="hint">
              Add this URL and secret as a push webhook in your forge to sync on every push.
            </p>
            <div class="copy-row">
              <span class="code">${g.webhookUrl}</span>
              <bn-button
                variant="secondary"
                size="sm"
                @click=${() => void this.copy(g.webhookUrl!, "Webhook URL copied")}
                >Copy</bn-button
              >
            </div>
            <div class="copy-row">
              <span class="code">${g.webhookSecret}</span>
              <bn-button
                variant="secondary"
                size="sm"
                @click=${() => void this.copy(g.webhookSecret!, "Webhook secret copied")}
                >Copy</bn-button
              >
            </div>
          `
        : nothing}
    `;
  }

  // ---- Secrets ----

  private async loadSecrets() {
    this.secretsLoading = true;
    try {
      this.secrets = await listSecrets(this.workspaceId);
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.secretsLoading = false;
    }
  }

  private async submitSecret() {
    const name = this.secretName.trim();
    if (!name) {
      this.secretError = "Enter a secret name.";
      return;
    }
    if (!this.secretValue) {
      this.secretError = "Enter a value.";
      return;
    }
    this.savingSecret = true;
    this.secretError = "";
    try {
      await setSecret(this.workspaceId, name, this.secretValue);
      // Reload rather than patch locally: saving a name that the team also
      // defines flips that row from inherited to workspace-scoped.
      await this.loadSecrets();
      this.showAddSecret = false;
      this.secretName = "";
      this.secretValue = "";
      toast("Secret saved", "success");
    } catch (e) {
      // A 400 names the problem, e.g. a name the cluster reserves.
      this.secretError = detailedError(e);
    } finally {
      this.savingSecret = false;
    }
  }

  private async confirmSecretDelete() {
    const name = this.pendingSecretDelete;
    try {
      await deleteSecret(this.workspaceId, name);
      // Reload rather than filter locally: removing an override can uncover an
      // inherited team secret of the same name.
      await this.loadSecrets();
      toast("Secret deleted", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.pendingSecretDelete = "";
    }
  }

  private renderSecrets() {
    return html`<bn-card heading="Secrets" flush>
      <bn-button
        slot="actions"
        size="sm"
        variant="primary"
        @click=${() => {
          this.secretName = "";
          this.secretValue = "";
          this.secretError = "";
          this.showAddSecret = true;
        }}
        >Add secret</bn-button
      >
      ${this.secretsLoading && this.secrets.length === 0
        ? html`<bn-spinner block label="Loading secrets…"></bn-spinner>`
        : this.secrets.length === 0
          ? html`<bn-empty-state
              icon="🔑"
              heading="No secrets"
              message="Builds get secrets as environment variables. Team secrets apply to every workspace of the team."
            ></bn-empty-state>`
          : html`<bn-table
              .columns=${[
                { key: "name", label: "Name", mono: true },
                { key: "scope", label: "Scope", width: "1px" },
                { key: "actions", label: "", align: "right", width: "1px" },
              ] as BnColumn[]}
              .rows=${this.secrets.map(
                (s): BnRow => ({
                  name: s.reserved
                    ? html`${s.name}
                        <bn-badge variant="warning">reserved, builds ignore it</bn-badge>`
                    : s.name,
                  scope:
                    s.scope === "team"
                      ? html`<bn-badge variant="info">team</bn-badge>`
                      : s.scope === "repository"
                        ? html`<bn-badge variant="success">repository</bn-badge>`
                        : html`<bn-badge variant="neutral">workspace</bn-badge>`,
                  // Inherited secrets are managed on the team or repository
                  // page — deleting one here is not this scope's call.
                  actions:
                    s.scope !== "workspace"
                      ? nothing
                      : html`<bn-button
                          size="sm"
                          variant="danger"
                          @click=${() => (this.pendingSecretDelete = s.name)}
                          >Delete</bn-button
                        >`,
                }),
              )}
            ></bn-table>`}
    </bn-card>`;
  }

  // ---- Builds ----

  private async loadBuilds() {
    const ids = loadBuildIds(this.workspaceId);
    if (ids.length === 0) {
      this.builds = [];
      return;
    }
    this.buildsLoading = true;
    try {
      const results = await Promise.allSettled(ids.map((id) => getBuild(id)));
      const alive: string[] = [];
      const builds: Build[] = [];
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          alive.push(ids[i]!);
          builds.push(r.value);
        }
        // rejected (e.g. build_not_found) — drop the id from history
      });
      if (alive.length !== ids.length) saveBuildIds(this.workspaceId, alive);
      this.builds = builds.sort((a, b) => (a.created < b.created ? 1 : -1));
    } finally {
      this.buildsLoading = false;
      this.schedulePoll();
    }
  }

  /** Upserts a build into the rendered list, keeping it sorted newest-first. */
  private mergeBuild(build: Build) {
    this.builds = this.builds.some((b) => b.id === build.id)
      ? this.builds.map((b) => (b.id === build.id ? build : b))
      : [build, ...this.builds].sort((a, b) => (a.created < b.created ? 1 : -1));
  }

  /**
   * Subscribes to the workspace's builds realtime events. Status flips update
   * the history live, and create events surface builds this client never
   * started (git webhooks, other tabs/API callers) — those ids join the
   * localStorage tracking. Polling remains the fallback.
   */
  private startBuildsSubscription() {
    this.stopBuildsSubscription();
    this.stopBuildsLive = subscribeLive({
      collection: "builds",
      topic: "*",
      filter: pb.filter("workspace = {:id}", { id: this.workspaceId }),
      fields: "id,status",
      onEvent: (action, id) => void this.onBuildEvent(action, id),
      onUp: () => {
        this.buildsLiveUp = true;
        this.stopPoll();
        void this.pollBuilds();
      },
      onDown: () => this.schedulePoll(),
    });
  }

  private stopBuildsSubscription() {
    this.stopBuildsLive?.();
    this.stopBuildsLive = undefined;
    this.buildsLiveUp = false;
  }

  private async onBuildEvent(action: string, id: string) {
    if (action === "delete") {
      // Retention sweep — drop the build from history.
      this.builds = this.builds.filter((b) => b.id !== id);
      saveBuildIds(
        this.workspaceId,
        loadBuildIds(this.workspaceId).filter((x) => x !== id),
      );
      return;
    }
    // Create and update handled uniformly: an update for an untracked id means
    // the build started while this page wasn't watching.
    trackBuildId(this.workspaceId, id);
    try {
      this.mergeBuild(await getBuild(id));
    } catch {
      /* transient refetch failure — the next event catches up */
    }
  }

  private schedulePoll() {
    if (this.buildsLiveUp) return;
    const anyLive = this.builds.some((b) => !isTerminal(b.status));
    if (anyLive && this.pollTimer === undefined) {
      this.pollTimer = window.setInterval(() => void this.pollBuilds(), POLL_MS);
    } else if (!anyLive) {
      this.stopPoll();
    }
  }

  private stopPoll() {
    if (this.pollTimer !== undefined) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  private async pollBuilds() {
    const live = this.builds.filter((b) => !isTerminal(b.status));
    if (live.length === 0) {
      this.stopPoll();
      return;
    }
    const updates = await Promise.allSettled(live.map((b) => getBuild(b.id)));
    const byId = new Map<string, Build>();
    updates.forEach((r) => {
      if (r.status === "fulfilled") byId.set(r.value.id, r.value);
    });
    this.builds = this.builds.map((b) => byId.get(b.id) ?? b);
    this.schedulePoll();
  }

  private async buildNow() {
    this.building = true;
    try {
      const { buildId, status } = await startBuild(this.workspaceId);
      // trackBuildId/mergeBuild, not a blind prepend: the realtime create event
      // for this very build usually arrives before startBuild resolves, and
      // onBuildEvent has already inserted it.
      trackBuildId(this.workspaceId, buildId);
      toast(status === "queued" ? "Build queued" : "Build started", "success");
      this.mergeBuild(await getBuild(buildId));
      this.schedulePoll();
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.building = false;
    }
  }

  private async openBuildDetail(build: Build) {
    this.detailBuild = build;
    this.detailLog = "";
    this.detailArtifacts = [];
    this.detailLoading = true;
    try {
      const [log, artifacts] = await Promise.all([
        getLog(build.id).catch(() => ""),
        listArtifacts(build.id).catch((): Artifact[] => []),
      ]);
      this.detailLog = log;
      this.detailArtifacts = artifacts;
    } finally {
      this.detailLoading = false;
    }
  }

  private async downloadArtifact(buildId: string, path: string) {
    try {
      const blob = await getArtifactBlob(buildId, path);
      downloadBlob(blob, path.split("/").pop() ?? path);
    } catch (e) {
      toast(humanError(e), "error");
    }
  }

  private renderBuilds() {
    return html`<bn-card heading="Builds">
      <div slot="actions" class="actions-row">
        <bn-button size="sm" variant="ghost" @click=${() => this.loadBuilds()}>Refresh</bn-button>
        <bn-button size="sm" variant="primary" ?loading=${this.building} @click=${() =>
          this.buildNow()}>Build now</bn-button>
      </div>
      ${this.buildsLoading && this.builds.length === 0
        ? html`<bn-spinner block label="Loading builds…"></bn-spinner>`
        : this.builds.length === 0
          ? html`<bn-empty-state
              icon="⚙"
              heading="No builds yet"
              message="Run a build to compile this workspace. Builds started here are tracked in this browser."
            ></bn-empty-state>`
          : html`<bn-table
              .columns=${[
                { key: "status", label: "Status" },
                { key: "created", label: "Started", mono: true },
                { key: "duration", label: "Duration", mono: true },
                { key: "actions", label: "", align: "right", width: "1px" },
              ] as BnColumn[]}
              .rows=${this.builds.map((b): BnRow => this.buildRow(b))}
            ></bn-table>`}
    </bn-card>`;
  }

  private buildRow(b: Build): BnRow {
    const live = !isTerminal(b.status);
    const duration =
      b.started && b.finished
        ? `${Math.max(0, Math.round((new Date(b.finished).getTime() - new Date(b.started).getTime()) / 1000))}s`
        : "—";
    return {
      status: html`<bn-badge .variant=${buildStatusVariant(b.status)} ?dot=${live}
        >${b.status}</bn-badge
      >`,
      created: b.created ? new Date(b.created).toLocaleString() : "—",
      duration,
      actions: html`<div class="actions-row" style="justify-content:flex-end">
        <bn-button size="sm" variant="ghost" @click=${() => this.openBuildDetail(b)}
          >Details</bn-button
        >
        <bn-button size="sm" variant="ghost" @click=${() => navigate(`/builds/${b.id}`)}
          >Open</bn-button
        >
      </div>`,
    };
  }

  // ---- Live artefacts ----

  private async loadDeployments() {
    this.deploymentsLoading = true;
    try {
      this.deployments = await listDeployments(this.workspaceId);
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.deploymentsLoading = false;
    }
  }

  /**
   * The deployments this UI can open in a tab (see canOpen). It decides whether
   * the header offers a Run button at all, and that button opens the first of
   * them — the card's own per-deployment Run is where a workspace publishing
   * several artefacts picks one. Derived from the rows-only listing on purpose:
   * driving it from discovery would spawn a sandbox every time anyone opened a
   * workspace, and would 403 outright for viewers.
   */
  private get openable(): LiveDeployment[] {
    return this.deployments.filter(canOpen);
  }

  /** Upserts a deployment the server just answered with, in place. */
  private mergeDeployment(d: LiveDeployment) {
    this.deployments = this.deployments.map((x) => (x.id === d.id ? d : x));
  }

  /**
   * openLive opens the published report in a new tab. Team visibility is the
   * only one that needs a credential the new tab cannot set as a header, so it
   * gets a freshly minted short-lived token in the `t` parameter; a public
   * deployment opens at its bare URL. Never called for "token" visibility —
   * canOpen keeps those out, because the only credential that would work is one
   * this UI does not hold.
   */
  private async openLive(d: LiveDeployment) {
    this.openingLive = d.id;
    try {
      const url =
        d.visibility === "team"
          ? `${d.url}?t=${encodeURIComponent((await mintLiveToken(this.workspaceId, d.id)).token)}`
          : d.url!;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.openingLive = "";
    }
  }

  private async publishNow(d: LiveDeployment) {
    this.liveBusy = d.id;
    try {
      this.mergeDeployment(await publishDeployment(this.workspaceId, d.id));
      toast("Published. The new snapshot is live.", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.liveBusy = "";
    }
  }

  private async toggleLive(d: LiveDeployment, run: boolean) {
    this.liveBusy = d.id;
    try {
      this.mergeDeployment(
        run
          ? await startDeployment(this.workspaceId, d.id)
          : await stopDeployment(this.workspaceId, d.id),
      );
      toast(run ? "Live report started" : "Live report stopped", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.liveBusy = "";
    }
  }

  private async confirmLiveDelete() {
    const d = this.pendingLiveDelete;
    if (!d) return;
    try {
      await deleteDeployment(this.workspaceId, d.id);
      this.deployments = this.deployments.filter((x) => x.id !== d.id);
      toast("Live report deleted", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.pendingLiveDelete = null;
    }
  }

  /**
   * openPublish is the ONE place artefact discovery is called from. That route
   * indexes the project inside a bino sandbox, so it costs what a lint costs
   * and is member+ for exactly that reason: calling it from load() would start
   * a container every time anyone opened a workspace, and would 403 for every
   * viewer. It runs here, once, because the user asked to publish something.
   *
   * With deployments on and a repository, publishing goes to the
   * repository's deployments page instead: a deployment is made there at a
   * commit, and one artefact may have several. The modal and its filter
   * below serve only the case with deployments off.
   */
  private openPublish() {
    const repo = this.workspace?.repository;
    if (this.deploymentsOn && repo) {
      navigate(`/repositories/${encodeURIComponent(repo)}/deployments`);
      return;
    }
    this.pubArtefact = "";
    this.pubVisibility = "team";
    this.pubToken = "";
    this.pubEmbed = "";
    this.pubIdle = "600";
    this.pubSessionTtl = "2592000";
    this.pubError = "";
    this.publishedToken = "";
    this.showPublish = true;
    void this.discover();
  }

  private closePublish() {
    this.showPublish = false;
    this.pubToken = "";
    this.publishedToken = "";
  }

  private async discover() {
    this.candidatesLoading = true;
    this.candidates = [];
    this.pubError = "";
    try {
      // Only artefacts without a deployment: with deployments off the
      // workspace+artefact pair is unique, so offering a published one would
      // just earn a 409. With deployments on this modal is not used (see
      // openPublish): an artefact may have several deployments then.
      this.candidates = (await discoverArtefacts(this.workspaceId)).filter(
        (c) => c.available && !c.deployment,
      );
      this.pubArtefact = this.candidates[0]?.artefact ?? "";
    } catch (e) {
      this.pubError = humanError(e);
    } finally {
      this.candidatesLoading = false;
    }
  }

  private async submitPublish() {
    if (this.pubArtefact === "") {
      this.pubError = "Choose an artefact to publish.";
      return;
    }
    // Only "token" visibility has an access token at all. The field is hidden
    // for the other two, so a value left behind by a user who typed one and
    // then changed their mind must not be sent: the server would burn it
    // against the collection-wide-unique token hash, store it on a deployment
    // that never checks it, and this modal would demand they copy a secret
    // nothing will ever ask for.
    const token = this.pubVisibility === "token" ? this.pubToken : "";
    if (this.pubVisibility === "token" && token.length < 16) {
      this.pubError = "Token visibility needs an access token of at least 16 characters.";
      return;
    }
    this.publishing = true;
    this.pubError = "";
    try {
      const d = await createDeployment(this.workspaceId, {
        artefact: this.pubArtefact,
        visibility: this.pubVisibility,
        ...(token === "" ? {} : { token }),
        embedDomains: parseHosts(this.pubEmbed),
        idleTimeoutSeconds: Number(this.pubIdle),
        sessionTtlSeconds: Number(this.pubSessionTtl),
      });
      this.deployments = [...this.deployments, d];
      toast("Live report published", "success");
      // The server kept only this token's SHA-256, so the input's copy is now
      // the last one in existence: hand it to the reveal panel and drop it
      // here, and close outright when there was no token to show.
      this.pubToken = "";
      this.publishedToken = token;
      if (token === "") this.closePublish();
    } catch (e) {
      this.pubError = humanError(e);
    } finally {
      this.publishing = false;
    }
  }

  /**
   * openEdit seeds the settings form from the row. The access token is the one
   * field it cannot prefill — only its hint ever comes back — so an empty box
   * means "keep the stored one" rather than "there is none".
   */
  private openEdit(d: LiveDeployment) {
    this.pubVisibility = d.visibility;
    this.pubToken = "";
    this.pubEmbed = d.embedDomains.join(" ");
    this.pubIdle = String(d.idleTimeoutSeconds);
    this.pubSessionTtl = String(d.sessionTtlSeconds);
    this.pubError = "";
    this.editTarget = d;
  }

  private closeEdit() {
    this.editTarget = null;
    this.pubToken = "";
  }

  /**
   * submitEdit changes how the deployment is SERVED. It exists because publish
   * does not: publishing re-snapshots and touches none of these fields, and
   * delete-and-republish draws a new slug, which breaks every URL and every
   * iframe the customer has already handed out.
   *
   * Two rules about the token. An empty box keeps whatever is stored, since
   * this UI cannot read it back to re-send it. And leaving "token" visibility
   * CLEARS it: the serving plane only ever compares a token hash for that one
   * visibility, so a leftover would be a credential nothing checks, shown on
   * the card beside a "public" badge and holding its string hostage against the
   * collection-wide-unique hash.
   */
  private async submitEdit() {
    const d = this.editTarget;
    if (!d) return;
    const token = this.pubVisibility === "token" ? this.pubToken : "";
    // Empty is only legal when there is a stored token to keep.
    const wantsToken = !d.hasToken || token !== "";
    if (this.pubVisibility === "token" && wantsToken && token.length < 16) {
      this.pubError = "Token visibility needs an access token of at least 16 characters.";
      return;
    }
    const req: UpdateRequest = {
      visibility: this.pubVisibility,
      embedDomains: parseHosts(this.pubEmbed),
      idleTimeoutSeconds: Number(this.pubIdle),
      sessionTtlSeconds: Number(this.pubSessionTtl),
    };
    if (this.pubVisibility !== "token") req.token = "";
    else if (token !== "") req.token = token;

    this.savingEdit = true;
    this.pubError = "";
    try {
      this.mergeDeployment(await updateDeployment(this.workspaceId, d.id, req));
      toast("Live report settings saved", "success");
      this.closeEdit();
    } catch (e) {
      this.pubError = humanError(e);
    } finally {
      this.savingEdit = false;
    }
  }

  private openLinkModal(d: LiveDeployment) {
    this.linkTtl = "3600";
    this.linkParams = "";
    this.linkError = "";
    this.mintedLink = "";
    this.linkTarget = d;
  }

  /**
   * closeLinkModal drops the minted link along with the modal. A viewer link is
   * a bearer capability valid for up to 24 hours, so it gets closePublish's
   * treatment rather than being left in reactive state for the rest of the
   * page's life: the panel says it is not shown again, and this is what makes
   * that true of the value and not merely of the pixels.
   */
  private closeLinkModal() {
    this.linkTarget = null;
    this.mintedLink = "";
    this.linkParams = "";
  }

  private async submitLink() {
    const d = this.linkTarget;
    if (!d) return;
    this.mintingLink = true;
    this.linkError = "";
    try {
      const { url } = await mintViewerLink(this.workspaceId, d.id, {
        ttlSeconds: Number(this.linkTtl),
        params: parseParams(this.linkParams),
      });
      this.mintedLink = url;
    } catch (e) {
      this.linkError = humanError(e);
    } finally {
      this.mintingLink = false;
    }
  }

  private async confirmLinkRotate() {
    const d = this.pendingLinkRotate;
    if (!d) return;
    this.rotatingLinkKey = true;
    try {
      await rotateLinkKey(this.workspaceId, d.id);
      toast("Viewer links invalidated and devices signed out", "success");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.rotatingLinkKey = false;
      this.pendingLinkRotate = null;
    }
  }

  /**
   * The curl for the very call the form above makes, built from the values
   * currently typed in it. A viewer link is minted per end user by the
   * customer's own backend, so the UI's job is to make that call discoverable,
   * not to be the place it happens.
   */
  private linkCurl(d: LiveDeployment): string {
    const body = JSON.stringify({
      ttlSeconds: Number(this.linkTtl),
      params: parseParams(this.linkParams),
    });
    // A deployment of a repository shows the canonical route; the workspace
    // route is its alias and goes away.
    const path = d.repositoryId
      ? `/v1/repositories/${d.repositoryId}/deployments/${d.id}/links`
      : `/v1/workspaces/${d.workspaceId}/live-artefacts/${d.id}/links`;
    return [
      `curl -X POST ${window.location.origin}${path} \\`,
      `  -H "Authorization: Bearer $BINO_API_KEY" \\`,
      `  -H "Content-Type: application/json" \\`,
      `  -d '${body}'`,
    ].join("\n");
  }

  private renderLive() {
    return html`<bn-card heading="Live reports">
      <div slot="actions" class="actions-row">
        <bn-button size="sm" variant="ghost" @click=${() => this.loadDeployments()}
          >Refresh</bn-button
        >
        <bn-button size="sm" variant="primary" @click=${() => this.openPublish()}>Publish</bn-button>
      </div>
      ${this.deploymentsLoading && this.deployments.length === 0
        ? html`<bn-spinner block label="Loading live reports…"></bn-spinner>`
        : this.deployments.length === 0
          ? html`<bn-empty-state
              icon="◉"
              heading="Nothing published"
              message="Publish a LiveReportArtefact to serve it at a permanent URL that stays up between visits, instead of rebuilding it per request."
            ></bn-empty-state>`
          : this.deployments.map((d) => this.renderDeployment(d))}
    </bn-card>`;
  }

  private renderDeployment(d: LiveDeployment) {
    const live = d.status === "live";
    const busy = this.liveBusy === d.id;
    return html`<div class="deploy">
      <div class="deploy-head">
        <span class="deploy-name">${d.artefact}</span>
        <bn-badge .variant=${liveStatusVariant(d.status)} ?dot=${live}>${d.status}</bn-badge>
      </div>
      <div class="deploy-rows">
        <div class="deploy-row">
          <span class="deploy-label">Visibility</span>
          <bn-badge .variant=${d.visibility === "public" ? "warning" : "neutral"}
            >${d.visibility}</bn-badge
          >
          ${d.hasToken ? html`<span class="mono">token ••••${d.tokenHint}</span>` : nothing}
        </div>
        <div class="deploy-row">
          <span class="deploy-label">Instance</span>
          <span>${idleLabel(d.idleTimeoutSeconds)}</span>
        </div>
        <div class="deploy-row">
          <span class="deploy-label">Device sessions</span>
          <span>${sessionLabel(d.sessionTtlSeconds)}</span>
        </div>
        <div class="deploy-row">
          <span class="deploy-label">Last published</span>
          ${d.snapshotAt
            ? html`<span>${new Date(d.snapshotAt).toLocaleString()}</span>
                ${d.snapshotSource
                  ? html`<span class="mono"
                      >${d.snapshotSource === "manual"
                        ? "manual"
                        : d.snapshotSource.slice(0, 8)}</span
                    >`
                  : nothing}`
            : html`<span>Never</span>`}
        </div>
        <div class="deploy-row">
          <span class="deploy-label">Embed domains</span>
          <span class="mono">${d.embedDomains.length > 0 ? d.embedDomains.join(" ") : "—"}</span>
        </div>
      </div>
      ${d.url
        ? html`<div class="copy-row">
            <span class="code">${d.url}</span>
            <bn-button
              variant="secondary"
              size="sm"
              @click=${() => void this.copy(d.url!, "Public URL copied")}
              >Copy</bn-button
            >
          </div>`
        : html`<p class="hint">
            Live hosting is not set up on this server, so this live report has no public URL yet.
            The snapshot is kept. Ask your admin to set up live hosting.
          </p>`}
      ${this.renderEmbed(d)}
      <div class="actions-row">
        ${canOpen(d)
          ? html`<bn-button
              size="sm"
              variant="secondary"
              ?loading=${this.openingLive === d.id}
              @click=${() => this.openLive(d)}
              >Open</bn-button
            >`
          : nothing}
        <bn-button size="sm" variant="secondary" ?loading=${busy} @click=${() => this.publishNow(d)}
          >Publish</bn-button
        >
        ${live
          ? html`<bn-button
              size="sm"
              variant="ghost"
              ?disabled=${busy}
              @click=${() => this.toggleLive(d, false)}
              >Stop</bn-button
            >`
          : html`<bn-button
              size="sm"
              variant="ghost"
              ?disabled=${busy || !d.snapshot}
              @click=${() => this.toggleLive(d, true)}
              >Start</bn-button
            >`}
        <bn-button size="sm" variant="ghost" @click=${() => this.openEdit(d)}>Settings</bn-button>
        <bn-button size="sm" variant="ghost" @click=${() => this.openLinkModal(d)}
          >Viewer link</bn-button
        >
        <bn-button size="sm" variant="ghost" @click=${() => (this.pendingLinkRotate = d)}
          >Invalidate links</bn-button
        >
        <bn-button size="sm" variant="danger" @click=${() => (this.pendingLiveDelete = d)}
          >Delete</bn-button
        >
      </div>
    </div>`;
  }

  /**
   * The iframe snippet, for the two visibilities an anonymous embedding page
   * can actually reach. A team-visibility deployment re-resolves the viewer's
   * team role per request, so there is no snippet to give.
   */
  private renderEmbed(d: LiveDeployment) {
    if (!d.url || d.visibility === "team") return nothing;
    const src = d.visibility === "token" ? `${d.url}?t=YOUR_ACCESS_TOKEN` : d.url;
    const snippet = `<iframe src="${src}" width="100%" height="800" style="border:0"></iframe>`;
    return html`
      <div class="section-label">Embed</div>
      <div class="copy-row">
        <pre class="log">${snippet}</pre>
        <bn-button
          variant="secondary"
          size="sm"
          @click=${() => void this.copy(snippet, "Embed snippet copied")}
          >Copy</bn-button
        >
      </div>
      ${d.embedDomains.length === 0
        ? html`<p class="hint">
            This live report allows no embed domains, so browsers refuse to frame it. Add the hosts
            under Settings. Publishing again does not change this.
          </p>`
        : nothing}
    `;
  }

  // ---- Preview ----

  private async openPreview() {
    this.openingPreview = true;
    try {
      await mintPreviewToken(this.workspaceId);
      window.open(previewUrl(this.workspaceId), "_blank", "noopener");
    } catch (e) {
      toast(humanError(e), "error");
    } finally {
      this.openingPreview = false;
    }
  }

  // ---- Workspace delete ----

  private async confirmDeleteWorkspace() {
    this.deletingWs = true;
    try {
      await deleteWorkspace(this.workspaceId);
      toast("Workspace deleted", "success");
      navigate("/workspaces");
    } catch (e) {
      toast(humanError(e), "error");
      this.deletingWs = false;
      this.confirmDeleteWs = false;
    }
  }

  override render() {
    if (this.loading) return html`<bn-spinner block label="Loading workspace…"></bn-spinner>`;
    if (this.error || !this.workspace) {
      return html`<bn-empty-state
        icon="⚠"
        heading="Couldn’t load workspace"
        message=${this.error || "Workspace not found."}
      >
        <bn-button slot="actions" variant="secondary" @click=${() => navigate("/workspaces")}
          >Back to workspaces</bn-button
        >
      </bn-empty-state>`;
    }

    return html`
      <div class="crumbs">
        <a
          href="/workspaces"
          @click=${(e: Event) => {
            e.preventDefault();
            navigate("/workspaces");
          }}
          >Workspaces</a
        >
        <span aria-hidden="true">/</span>
      </div>
      <div class="head">
        <div class="titles">
          <h1>${this.workspace.name}</h1>
          ${this.renderRepoRow()} ${this.renderTagRow()}
        </div>
        <span class="spacer"></span>
        <bn-button variant="secondary" ?loading=${this.openingPreview} @click=${() =>
          this.openPreview()}>Open preview</bn-button>
        ${this.openable.length > 0
          ? html`<bn-button
              variant="secondary"
              ?loading=${this.openingLive !== ""}
              @click=${() => this.openLive(this.openable[0]!)}
              >Open live report</bn-button
            >`
          : nothing}
        <bn-button variant="danger" @click=${() => (this.confirmDeleteWs = true)}>Delete</bn-button>
      </div>

      <div class="sections">
        ${this.renderFiles()} ${this.renderGit()} ${this.renderSecrets()} ${this.renderBuilds()}
        ${this.renderLive()}
      </div>

      ${this.renderModals()}
    `;
  }

  /** The header link to the workspace's repository and the branch it shows. */
  private renderRepoRow() {
    const repo = this.workspace?.repository;
    if (!repo) return nothing;
    const href = `/repositories/${encodeURIComponent(repo)}`;
    return html`<div class="repo-row">
      <a
        href=${href}
        @click=${(e: Event) => {
          e.preventDefault();
          navigate(href);
        }}
        >Repository</a
      >
      ${this.workspace?.branch
        ? html`<span>branch <span class="mono">${this.workspace.branch}</span></span>`
        : nothing}
      ${this.workspace?.defaultBranch ? html`<bn-badge>default</bn-badge>` : nothing}
      ${this.workspace?.pinned ? html`<bn-badge>pinned</bn-badge>` : nothing}
    </div>`;
  }

  /**
   * The header tag row: attached tags with a one-click detach, plus the button
   * that opens the attach editor. Detach is the frequent action and gets the ×
   * directly on the chip; attaching is deliberate and opens a modal, since the
   * component kit has no popover primitive.
   */
  private renderTagRow() {
    const tags = this.workspace?.tags ?? [];
    return html`<div class="tag-row">
      ${tags.map(
        (t) => html`<bn-tag
          removable
          .name=${t.name}
          color=${t.color}
          ?disabled=${this.tagBusy}
          @remove=${() => void this.setTags(tags.filter((x) => x.id !== t.id))}
        ></bn-tag>`,
      )}
      <bn-button variant="ghost" size="sm" @click=${() => this.openTagEditor()}
        >${tags.length === 0 ? "Add tags" : "Edit tags"}</bn-button
      >
    </div>`;
  }

  private renderTagModal() {
    const attached = this.workspace?.tags ?? [];
    const attachedIds = new Set(attached.map((t) => t.id));
    return html`
      <bn-modal ?open=${this.showTags} heading="Tags" @close=${() => (this.showTags = false)}>
        <p>
          Tags belong to the team, so every workspace on it can use them. Changes save
          immediately.
        </p>
        ${this.tagCatalog.length > 0
          ? html`<div class="tag-row">
              ${this.tagCatalog.map((t) => {
                const on = attachedIds.has(t.id);
                return html`<bn-tag
                  selectable
                  .name=${t.name}
                  color=${t.color}
                  ?selected=${on}
                  ?disabled=${this.tagBusy}
                  @toggle=${() =>
                    void this.setTags(
                      on ? attached.filter((x) => x.id !== t.id) : [...attached, t],
                    )}
                ></bn-tag>`;
              })}
            </div>`
          : html`<p>This team has no tags yet. Create the first one below.</p>`}
        <bn-field label="New tag" error=${this.tagError}>
          <bn-input
            placeholder="production"
            .value=${this.newTagName}
            ?invalid=${this.tagError !== ""}
            @input=${(e: CustomEvent<{ value: string }>) => (this.newTagName = e.detail.value)}
            @keydown=${(e: KeyboardEvent) => {
              if (e.key === "Enter") void this.createAndAttach();
            }}
          ></bn-input>
        </bn-field>
        <bn-field label="Color">
          <bn-color-picker
            label="Tag color"
            .value=${this.newTagColor}
            @change=${(e: CustomEvent<{ value: BnTagColor }>) =>
              (this.newTagColor = e.detail.value)}
          ></bn-color-picker>
        </bn-field>
        <bn-button
          variant="secondary"
          ?loading=${this.creatingTag}
          @click=${() => void this.createAndAttach()}
          >Create &amp; attach</bn-button
        >
        <bn-button slot="footer" variant="primary" @click=${() => (this.showTags = false)}
          >Done</bn-button
        >
      </bn-modal>
    `;
  }

  private renderModals() {
    return html`
      ${this.renderTagModal()}
      <bn-modal
        ?open=${this.showNewFile}
        heading="New file"
        .width=${560}
        @close=${() => (this.showNewFile = false)}
      >
        <bn-field label="Path" required hint="Slash-separated, e.g. src/report.md" error=${this.newFileError}>
          <bn-input
            mono
            .value=${this.newFilePath}
            placeholder="report.md"
            @input=${(e: CustomEvent<{ value: string }>) => (this.newFilePath = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Contents">
          <textarea
            class="modal-textarea"
            .value=${this.newFileContent}
            spellcheck="false"
            @input=${(e: Event) => (this.newFileContent = (e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </bn-field>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.showNewFile = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.creatingFile}
          @click=${() => this.submitNewFile()}
          >Create</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.pendingFileDelete !== ""}
        heading="Delete file?"
        @close=${() => (this.pendingFileDelete = "")}
      >
        <p>
          Delete <strong>${this.pendingFileDelete}</strong>? This cannot be undone.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingFileDelete = "")}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.confirmFileDelete()}
          >Delete</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.showGitModal}
        heading=${this.gitLocked
          ? "Edit git connection"
          : this.gitRepository
            ? "Connect git forge"
            : "Connect repository"}
        .width=${560}
        @close=${() => (this.showGitModal = false)}
      >
        <bn-field
          label="Repository URL"
          required
          hint="HTTPS clone URL, e.g. https://github.com/acme/reports.git"
          error=${this.gitError}
        >
          <bn-input
            mono
            .value=${this.gitRepoUrl}
            placeholder="https://github.com/acme/reports.git"
            @input=${(e: CustomEvent<{ value: string }>) => (this.gitRepoUrl = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field
          label="Branch"
          hint=${this.gitRepository
            ? "Becomes the repository's default branch, and this workspace follows it."
            : ""}
        >
          <bn-input
            mono
            .value=${this.gitBranch}
            placeholder="main"
            @input=${(e: CustomEvent<{ value: string }>) => (this.gitBranch = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field
          label="Access token"
          hint=${this.gitLocked && this.git?.hasToken
            ? "Leave empty to keep the current token. A URL on another host clears it."
            : "Leave empty for public repositories."}
        >
          <bn-input
            type="password"
            .value=${this.gitToken}
            @input=${(e: CustomEvent<{ value: string }>) => (this.gitToken = e.detail.value)}
          ></bn-input>
        </bn-field>
        <div class="warn">
          <span class="warn-icon" aria-hidden="true">!</span>
          ${this.gitRepository && !this.gitLocked
            ? html`<span
                >This repository becomes a mirror of the forge. The forge's branches replace the
                branches here, and branch workspaces that exist only here are removed, except the
                default and pinned ones. Their commits are kept in the repository's history but no
                longer show as branches. ${TAKE_OVER_WARNING}</span
              >`
            : this.gitRepository
              ? html`<span
                  >Existing workspace files are replaced by the repository contents on the first
                  sync. ${TAKE_OVER_WARNING}</span
                >`
              : html`<span
                  >Existing workspace files are replaced by the repository contents on the first
                  sync.</span
                >`}
        </div>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.showGitModal = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.savingGit}
          @click=${() => this.submitGit()}
          >${this.gitLocked ? "Save" : "Connect"}</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.confirmGitDisconnect}
        heading=${this.gitRepository ? "Disconnect git forge?" : "Disconnect repository?"}
        @close=${() => (this.confirmGitDisconnect = false)}
      >
        ${this.gitRepository
          ? html`<p>
              Disconnect <strong>${this.git?.repoUrl}</strong>? Syncing stops. The repository stays
              here with its full history and its branches, and you can edit the files and push to
              it directly.
            </p>`
          : html`<p>
              Disconnect <strong>${this.git?.repoUrl}</strong>? Syncing stops. The synced files stay,
              and you can edit them here again.
            </p>`}
        <bn-button slot="footer" variant="ghost" @click=${() => (this.confirmGitDisconnect = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="danger"
          ?loading=${this.disconnectingGit}
          @click=${() => this.confirmGitDisconnectNow()}
          >Disconnect</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.showAddSecret}
        heading="Add secret"
        @close=${() => (this.showAddSecret = false)}
      >
        <bn-field label="Name" required hint="e.g. API_TOKEN" error=${this.secretError}>
          <bn-input
            mono
            .value=${this.secretName}
            placeholder="API_TOKEN"
            @input=${(e: CustomEvent<{ value: string }>) => (this.secretName = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-field label="Value" required hint="Write-only. It is never shown again.">
          <bn-input
            type="password"
            .value=${this.secretValue}
            @input=${(e: CustomEvent<{ value: string }>) => (this.secretValue = e.detail.value)}
          ></bn-input>
        </bn-field>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.showAddSecret = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.savingSecret}
          @click=${() => this.submitSecret()}
          >Save</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.pendingSecretDelete !== ""}
        heading="Delete secret?"
        @close=${() => (this.pendingSecretDelete = "")}
      >
        <p>
          Delete <strong>${this.pendingSecretDelete}</strong>? Builds no longer receive it. If the
          repository or the team has a secret with the same name, builds get that one instead.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingSecretDelete = "")}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.confirmSecretDelete()}
          >Delete</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.confirmDeleteWs}
        heading="Delete workspace?"
        @close=${() => (this.confirmDeleteWs = false)}
      >
        ${this.workspace?.defaultBranch
          ? html`<p>
              Delete <strong>${this.workspace?.name}</strong>? It holds the default branch of its
              repository, so the repository is deleted too: all its branch workspaces, its git
              history, its data drops and seals, and its backup. Files, secrets, and builds are
              removed permanently. This cannot be undone.
            </p>`
          : this.workspace?.repository
            ? html`<p>
                Delete <strong>${this.workspace.name}</strong>? Its secrets and builds are removed.
                The branch stays in the repository, and its next change brings the workspace back.
              </p>`
            : html`<p>
                Delete <strong>${this.workspace?.name}</strong>? Its files, secrets, and builds are
                removed permanently. This cannot be undone.
              </p>`}
        <bn-button slot="footer" variant="ghost" @click=${() => (this.confirmDeleteWs = false)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="danger"
          ?loading=${this.deletingWs}
          @click=${() => this.confirmDeleteWorkspace()}
          >Delete</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.detailBuild !== null}
        heading="Build details"
        .width=${720}
        @close=${() => (this.detailBuild = null)}
      >
        ${this.detailBuild
          ? html`<div class="detail-meta">
                <span
                  >Status:
                  <bn-badge .variant=${buildStatusVariant(this.detailBuild.status)}
                    >${this.detailBuild.status}</bn-badge
                  ></span
                >
                <span>ID: ${this.detailBuild.id}</span>
              </div>
              ${this.detailBuild.error
                ? html`<p class="hint" style="color:var(--bino-error)">${this.detailBuild.error}</p>`
                : nothing}
              ${this.detailLoading
                ? html`<bn-spinner block label="Loading log…"></bn-spinner>`
                : html`
                    <div class="section-label">Log</div>
                    ${this.detailLog
                      ? html`<pre class="log">${this.detailLog}</pre>`
                      : html`<p class="hint">No log output.</p>`}
                    <div class="section-label">Files</div>
                    ${this.detailArtifacts.length === 0
                      ? html`<p class="hint">No files.</p>`
                      : html`<bn-table
                          .columns=${[
                            { key: "path", label: "Path", mono: true },
                            { key: "size", label: "Size", align: "right", mono: true },
                            { key: "actions", label: "", align: "right", width: "1px" },
                          ] as BnColumn[]}
                          .rows=${this.detailArtifacts.map(
                            (a): BnRow => ({
                              path: a.path,
                              size: formatBytes(a.size),
                              actions: html`<button
                                class="link-btn"
                                @click=${() => this.downloadArtifact(this.detailBuild!.id, a.path)}
                              >
                                Download
                              </button>`,
                            }),
                          )}
                        ></bn-table>`}
                  `}`
          : nothing}
        <bn-button slot="footer" variant="ghost" @click=${() => (this.detailBuild = null)}
          >Close</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.showPublish}
        heading=${this.publishedToken ? "Live report published" : "Publish live report"}
        .width=${560}
        @close=${() => this.closePublish()}
      >
        ${this.publishedToken ? this.renderTokenReveal() : this.renderPublishForm()}
        ${this.publishedToken
          ? html`<bn-button slot="footer" variant="primary" @click=${() => this.closePublish()}
              >Done</bn-button
            >`
          : html`<bn-button slot="footer" variant="ghost" @click=${() => this.closePublish()}
                >Cancel</bn-button
              >
              <bn-button
                slot="footer"
                variant="primary"
                ?loading=${this.publishing}
                ?disabled=${this.pubArtefact === ""}
                @click=${() => this.submitPublish()}
                >Publish</bn-button
              >`}
      </bn-modal>

      <bn-modal
        ?open=${this.editTarget !== null}
        heading="Live report settings"
        .width=${560}
        @close=${() => this.closeEdit()}
      >
        ${this.editTarget ? this.renderServingFields(this.editTarget) : nothing}
        <bn-button slot="footer" variant="ghost" @click=${() => this.closeEdit()}>Cancel</bn-button>
        <bn-button
          slot="footer"
          variant="primary"
          ?loading=${this.savingEdit}
          @click=${() => this.submitEdit()}
          >Save</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.linkTarget !== null}
        heading="Generate viewer link"
        .width=${720}
        @close=${() => this.closeLinkModal()}
      >
        ${this.linkTarget ? this.renderLinkModal(this.linkTarget) : nothing}
        ${this.mintedLink
          ? html`<bn-button slot="footer" variant="primary" @click=${() => this.closeLinkModal()}
              >Done</bn-button
            >`
          : html`<bn-button slot="footer" variant="ghost" @click=${() => this.closeLinkModal()}
                >Cancel</bn-button
              >
              <bn-button
                slot="footer"
                variant="primary"
                ?loading=${this.mintingLink}
                @click=${() => this.submitLink()}
                >Generate</bn-button
              >`}
      </bn-modal>

      <bn-modal
        ?open=${this.pendingLinkRotate !== null}
        heading="Invalidate all viewer links?"
        @close=${() => (this.pendingLinkRotate = null)}
      >
        <p>
          Every viewer link for <strong>${this.pendingLinkRotate?.artefact}</strong> stops working
          at once, and every signed-in device is signed out, installed home-screen apps included.
          Links are not tied to a person, so you cannot revoke a single one. Anyone who still needs
          access needs a new link.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingLinkRotate = null)}
          >Cancel</bn-button
        >
        <bn-button
          slot="footer"
          variant="danger"
          ?loading=${this.rotatingLinkKey}
          @click=${() => this.confirmLinkRotate()}
          >Invalidate all</bn-button
        >
      </bn-modal>

      <bn-modal
        ?open=${this.pendingLiveDelete !== null}
        heading="Delete live report?"
        @close=${() => (this.pendingLiveDelete = null)}
      >
        <p>
          Delete the live report <strong>${this.pendingLiveDelete?.artefact}</strong>? Its public
          URL stops resolving and its snapshots are removed. This cannot be undone.
        </p>
        <bn-button slot="footer" variant="ghost" @click=${() => (this.pendingLiveDelete = null)}
          >Cancel</bn-button
        >
        <bn-button slot="footer" variant="danger" @click=${() => this.confirmLiveDelete()}
          >Delete</bn-button
        >
      </bn-modal>
    `;
  }

  private renderPublishForm() {
    if (this.candidatesLoading) {
      return html`<bn-spinner block label="Looking for LiveReportArtefacts…"></bn-spinner>`;
    }
    if (this.candidates.length === 0) {
      return html`<p class="hint">
        ${this.pubError ||
        "Every LiveReportArtefact of this workspace is published already, or there is none. Add one and try again."}
      </p>`;
    }
    return html`
      <bn-field label="Artefact" required hint="A LiveReportArtefact of this workspace.">
        <select
          aria-label="Artefact"
          .value=${this.pubArtefact}
          @change=${(e: Event) => (this.pubArtefact = (e.target as HTMLSelectElement).value)}
        >
          ${this.candidates.map(
            (c) => html`<option value=${c.artefact} ?selected=${c.artefact === this.pubArtefact}>
              ${c.file ? `${c.artefact} (${c.file})` : c.artefact}
            </option>`,
          )}
        </select>
      </bn-field>
      ${this.renderServingFields(null)}
    `;
  }

  /**
   * The fields that describe how a deployment is SERVED, shared by publish
   * and by the settings modal because they are the same either way — the
   * only difference is that an existing deployment already holds a token this
   * UI can never show, so an empty box there means "keep it".
   *
   * The Instance select grows an extra option when the row holds a timeout the
   * list does not offer, which is what a deployment clamped up to the operator's
   * idle floor looks like (900 seconds, say); without it a perfectly valid row
   * would render a blank select.
   */
  private renderServingFields(existing: LiveDeployment | null) {
    return html`
      <bn-field
        label="Visibility"
        hint="Team: anyone on this workspace's team. Token: anyone holding the access token below. Public: anyone with the URL."
      >
        <select
          aria-label="Visibility"
          .value=${this.pubVisibility}
          @change=${(e: Event) =>
            (this.pubVisibility = (e.target as HTMLSelectElement).value as LiveVisibility)}
        >
          ${(["team", "token", "public"] as LiveVisibility[]).map(
            (v) => html`<option value=${v} ?selected=${v === this.pubVisibility}>${v}</option>`,
          )}
        </select>
      </bn-field>
      ${this.pubVisibility === "token"
        ? html`<bn-field
            label="Access token"
            ?required=${!existing?.hasToken}
            hint=${existing?.hasToken
              ? `Leave empty to keep the current token (••••${existing.tokenHint}). A new one replaces it for every viewer at once and is stored hashed, so keep your own copy.`
              : existing
                ? "16-256 characters, yours to choose. It is stored hashed and never shown back, so keep your own copy."
                : "16-256 characters, yours to choose. It is stored hashed and shown back to you exactly once, right after publishing."}
          >
            <bn-input
              type="password"
              .value=${this.pubToken}
              @input=${(e: CustomEvent<{ value: string }>) => (this.pubToken = e.detail.value)}
            ></bn-input>
          </bn-field>`
        : nothing}
      <bn-field
        label="Embed domains"
        hint="Hosts allowed to iframe the report, space- or comma-separated. Empty means no site may frame it."
      >
        <bn-input
          mono
          .value=${this.pubEmbed}
          placeholder="app.acme.com *.acme.com"
          @input=${(e: CustomEvent<{ value: string }>) => (this.pubEmbed = e.detail.value)}
        ></bn-input>
      </bn-field>
      <bn-field
        label="Instance"
        hint=${this.pubIdle === "0"
          ? "An always-on instance runs all the time. Your team's plan limits how many you can have."
          : "The instance stops when idle. The next visitor waits until it starts again."}
      >
        <select
          aria-label="Instance"
          .value=${this.pubIdle}
          @change=${(e: Event) => (this.pubIdle = (e.target as HTMLSelectElement).value)}
        >
          ${IDLE_OPTIONS.some((o) => o.value === this.pubIdle)
            ? nothing
            : html`<option value=${this.pubIdle} selected>
                ${idleLabel(Number(this.pubIdle))}
              </option>`}
          ${IDLE_OPTIONS.map(
            (o) => html`<option value=${o.value} ?selected=${o.value === this.pubIdle}>
              ${o.label}
            </option>`,
          )}
        </select>
      </bn-field>
      <bn-field
        label="Device sessions"
        hint=${this.pubSessionTtl === "0"
          ? "Off: every new browser session needs a viewer link, the access token or a team sign-in."
          : 'A device that opened the report with a valid link, token or sign-in stays signed in this long after its last visit. Each visit resets the timer. "Invalidate links" signs out all devices.'}
      >
        <select
          aria-label="Device sessions"
          .value=${this.pubSessionTtl}
          @change=${(e: Event) => (this.pubSessionTtl = (e.target as HTMLSelectElement).value)}
        >
          ${SESSION_OPTIONS.some((o) => o.value === this.pubSessionTtl)
            ? nothing
            : html`<option value=${this.pubSessionTtl} selected>
                ${sessionLabel(Number(this.pubSessionTtl))}
              </option>`}
          ${SESSION_OPTIONS.map(
            (o) => html`<option value=${o.value} ?selected=${o.value === this.pubSessionTtl}>
              ${o.label}
            </option>`,
          )}
        </select>
      </bn-field>
      ${this.pubError
        ? html`<p class="hint" style="color:var(--bino-error)">${this.pubError}</p>`
        : nothing}
    `;
  }

  /**
   * The access token's one and only appearance. bino Cloud kept nothing but its
   * SHA-256 and a four-character hint, so neither this UI nor support can ever
   * show it again — which is why the panel says so instead of implying a
   * "reveal" exists somewhere.
   */
  private renderTokenReveal() {
    return html`
      <div class="section-label">Access token</div>
      <div class="copy-row">
        <span class="code">${this.publishedToken}</span>
        <bn-button
          variant="secondary"
          size="sm"
          @click=${() => void this.copy(this.publishedToken, "Access token copied")}
          >Copy</bn-button
        >
      </div>
      <div class="warn" role="alert">
        <span class="warn-icon" aria-hidden="true">!</span>
        <span
          >Copy it now. It is not shown again. Later you only see its last four characters, and if
          you lose it, you have to set a new one.</span
        >
      </div>
    `;
  }

  private renderLinkModal(d: LiveDeployment) {
    if (this.mintedLink) {
      return html`
        <div class="section-label">Viewer link</div>
        <div class="copy-row">
          <span class="code">${this.mintedLink}</span>
          <bn-button
            variant="secondary"
            size="sm"
            @click=${() => void this.copy(this.mintedLink, "Viewer link copied")}
            >Copy</bn-button
          >
        </div>
        <p class="hint">
          The link is not shown again after you close this. You can generate a new one at any time.
          A single link cannot be revoked. "Invalidate links" voids all of them at once.
        </p>
      `;
    }
    const curl = this.linkCurl(d);
    return html`
      <p class="hint">
        A signed URL for someone without a bino Cloud account. Pinned parameters are part of the
        signature and override anything the visitor adds to the URL. They only keep rows away from
        the viewer if a DataSet's SQL filters on them. A component filter runs in the browser, so
        the viewer gets every row of its dataset.
      </p>
      <bn-field label="Expires after" hint="At most 24 hours.">
        <select
          aria-label="Expires after"
          .value=${this.linkTtl}
          @change=${(e: Event) => (this.linkTtl = (e.target as HTMLSelectElement).value)}
        >
          ${TTL_OPTIONS.map(
            (o) => html`<option value=${o.value} ?selected=${o.value === this.linkTtl}>
              ${o.label}
            </option>`,
          )}
        </select>
      </bn-field>
      <bn-field
        label="Pinned parameters"
        hint="One name=value per line, e.g. tenant=acme. Optional; at most 16."
      >
        <textarea
          class="modal-textarea"
          style="min-height:6rem"
          .value=${this.linkParams}
          spellcheck="false"
          @input=${(e: Event) => (this.linkParams = (e.target as HTMLTextAreaElement).value)}
        ></textarea>
      </bn-field>
      <div class="section-label">The same call from your backend</div>
      <div class="copy-row">
        <pre class="log">${curl}</pre>
        <bn-button
          variant="secondary"
          size="sm"
          @click=${() => void this.copy(curl, "curl command copied")}
          >Copy</bn-button
        >
      </div>
      ${this.linkError
        ? html`<p class="hint" style="color:var(--bino-error)">${this.linkError}</p>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-workspace": PageWorkspace;
  }
}
